import { useCallback, useEffect, useState } from "react";

/** localStorage / extension storage: show Archived next to To do and Done. */
export const ACTION_ITEMS_ARCHIVED_TAB_VISIBLE_KEY = "action_items_archived_tab_visible";

export const ARCHIVED_TAB_PREFERENCE_CHANGED_EVENT =
  "action-items-archived-tab-preference-changed";

export const ARCHIVED_TAB_VISIBLE_WEBAPP_DEFAULT = true;
export const ARCHIVED_TAB_VISIBLE_EXTENSION_DEFAULT = false;

/**
 * Whether the Archived stat tab is visible (webapp localStorage).
 *
 * @example
 * readArchivedTabVisiblePreference();
 * // true when unset (webapp default)
 *
 * @example
 * readArchivedTabVisiblePreference(false);
 * // false when unset (extension-style default)
 */
export const readArchivedTabVisiblePreference = (
  defaultVisible = ARCHIVED_TAB_VISIBLE_WEBAPP_DEFAULT
) => {
  if (typeof window === "undefined") return defaultVisible;
  const raw = window.localStorage.getItem(ACTION_ITEMS_ARCHIVED_TAB_VISIBLE_KEY);
  if (raw === null) return defaultVisible;
  return raw === "1";
};

/**
 * Persist Archived stat tab visibility for the webapp.
 *
 * @example
 * persistArchivedTabVisiblePreference(true);
 * readArchivedTabVisiblePreference(); // true
 */
export const persistArchivedTabVisiblePreference = (visible) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ACTION_ITEMS_ARCHIVED_TAB_VISIBLE_KEY, visible ? "1" : "0");
  window.dispatchEvent(new Event(ARCHIVED_TAB_PREFERENCE_CHANGED_EVENT));
};

/**
 * Sync Archived tab visibility with localStorage across settings and the tasks page.
 *
 * @example
 * const [showArchivedTab, setShowArchivedTab] = useArchivedTabVisiblePreference();
 * setShowArchivedTab(false);
 * // showArchivedTab === false; tasks header hides the Archived stat tab
 */
export const useArchivedTabVisiblePreference = () => {
  const [visible, setVisibleState] = useState(() => readArchivedTabVisiblePreference());

  useEffect(() => {
    const sync = () => setVisibleState(readArchivedTabVisiblePreference());
    window.addEventListener("storage", sync);
    window.addEventListener(ARCHIVED_TAB_PREFERENCE_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(ARCHIVED_TAB_PREFERENCE_CHANGED_EVENT, sync);
    };
  }, []);

  const setVisible = useCallback((nextVisible) => {
    persistArchivedTabVisiblePreference(Boolean(nextVisible));
    setVisibleState(Boolean(nextVisible));
  }, []);

  return [visible, setVisible];
};

/**
 * Status stat buttons for the action-items header.
 *
 * @example
 * buildActionItemsStatusStats({
 *   todoCount: 5,
 *   doneCount: 2,
 *   archivedCount: 1,
 *   showArchivedTab: true,
 * });
 * // [{ key: "pending", ... }, { key: "done", ... }, { key: "ignored", ... }]
 *
 * @example
 * buildActionItemsStatusStats({
 *   archivedCount: 3,
 *   sessionArchivedView: true,
 * });
 * // [{ key: "ignored", value: 3, label: "Archived", tier: "secondary" }]
 */
export const buildActionItemsStatusStats = ({
  todoCount,
  doneCount,
  archivedCount,
  showArchivedTab,
  sessionArchivedView = false,
}) => {
  if (sessionArchivedView) {
    return [
      {
        key: "ignored",
        value: archivedCount,
        label: "Archived",
        tier: "secondary",
      },
    ];
  }

  const stats = [
    { key: "pending", value: todoCount, label: "To do", tier: "primary" },
    { key: "done", value: doneCount, label: "Done", tier: "secondary" },
  ];
  if (showArchivedTab) {
    stats.push({
      key: "ignored",
      value: archivedCount,
      label: "Archived",
      tier: "secondary",
    });
  }
  return stats;
};
